/**
 * chunkFetchGuard — 场景分块下载硬化层（2026-11-02 P0）。
 *
 * 背景（11-02 老门东 47 人同场景活动实测）：出口带宽打满后，客户端与
 * nginx 之间形成「整文件重试风暴」——nginx send_timeout 默认 60s 掐掉零
 * 进度传输（日志 rt 恰为 60.000），引擎 XHR 立即整文件重来；叠加预载轮询
 * 的外层重入，单 IP 对同一 sh0.webp 连发 43 次请求，全场 56.8% 的请求是
 * 同 IP 同文件重复拉取（39,897/70,280，≈18GB 冗余流量）。chunk 实际是整文件
 * 200 响应（nginx expires 30d 强缓存），但被掐断/中止的传输不进浏览器缓存
 * ——风暴期间每次重试都是零字节起点的全量重拉。
 *
 * 方案：包住 PlayCanvas 导出的 http 单例（分块 webp/json 全部经
 * ImgParser/JsonHandler → http.get），对 /assets/scenes/ 下的 GET 注入：
 *   1. 停滞看门狗——STALL_MS 内零新字节即主动掐断（抢在 nginx 60s 之前），
 *      已收字节保留；
 *   2. 断点续传——重试带 Range: bytes=<received>-，服务器已回 Accept-Ranges
 *      （nginx 静态 206 实测可用），收到 206 续拼、被忽略（200）则整体重来；
 *   3. 指数退避 + 抖动 + 次数上限——替代「立即无限重试」，到顶后把错误交还
 *      引擎（GSplatAssetLoader 各自的 maxRetries 仍在，外层预载轮询的再入
 *      变成慢周期带进度的重试而非 20Hz 风暴）；
 *   4. 并发信号量——兜底所有走 http 单例的场景请求（引擎侧 maxConcurrentLoads
 *      之外的第二道闸）；
 *   5. 5xx/网络错误可重试，4xx 立即失败不重试。
 *
 * 非 /assets/scenes/ 的请求原样直通（返回真 XHR 句柄）。响应语义对齐引擎
 * http：blob/JSON/arraybuffer/text + options.cache===false 的 ts= 破缓存 +
 * options.progress（Asset）的 'progress' 事件。
 *
 * 验证：test/chunk-fetch-guard.test.mjs（本地 http 服务器模拟 500/停滞/断连/
 * Range，断言重试、续传、退避、最终结果字节级正确）。
 */

/** 引擎 http.get 的最小结构类型（避免运行时 import playcanvas，node 测试可直跑）。
 *  get 用方法签名（参数双变）且 callback 必选——与引擎 Http.get 的真签名对齐。 */
export interface HttpLike {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(url: any, options: any, callback: (err: any, response: any) => void): any;
}

interface GuardConfig {
    /** 单文件总尝试次数（1 次首试 + N-1 次重试）。 */
    maxAttempts: number;
    /** 重试基础延迟（ms），按 2^n 增长。 */
    backoffBaseMs: number;
    /** 重试延迟封顶（ms）。 */
    backoffCapMs: number;
    /** 退避抖动比例（0~1，乘在延迟上）。 */
    backoffJitter: number;
    /** 零字节停滞多久算死传输（ms）。须显著小于 nginx send_timeout 60s。 */
    stallMs: number;
    /** 响应头阶段等待上限（ms）。 */
    headerMs: number;
    /** 场景分块并发上限（引擎 maxConcurrentLoads 之外的第二道闸）。 */
    maxConcurrent: number;
}

const DEFAULT_CONFIG: GuardConfig = {
    maxAttempts: 7,
    backoffBaseMs: 400,
    backoffCapMs: 12000,
    backoffJitter: 0.3,
    stallMs: 12000,
    headerMs: 15000,
    maxConcurrent: 12
};

/** URL 是否属于本层接管范围：/assets/scenes/ 下的 GET（相对或同源绝对）。 */
export function shouldGuardSceneUrl(url: unknown, method?: string): boolean {
    if (method && method.toUpperCase() !== 'GET') return false;
    // 引擎调用方多数传字符串，个别传 { load, original } 对象
    const raw = typeof url === 'string'
        ? url
        : (url as { load?: unknown } | null)?.load;
    if (typeof raw !== 'string' || raw.length === 0) return false;
    let path: string;
    try {
        path = new URL(raw, (typeof location !== 'undefined' && location.href) || 'http://local/').pathname;
    } catch {
        return false;
    }
    return path.startsWith('/assets/scenes/');
}

interface FetchDeps {
    fetchImpl?: typeof fetch;
    now?: () => number;
    sleep?: (ms: number) => Promise<void>;
}

export interface GuardRunStats {
    attempts: number;
    retries: number;
    stalls: number;
    resumes: number;
    bytesFinal: number;
    bytesWasted: number;
    msTotal: number;
}

/**
 * 核心下载器：流式读 body + 停滞看门狗 + Range 续传 + 指数退避。
 * 返回完整字节（字节级校验 total）；失败到顶抛错。纯函数依赖注入，node 可测。
 */
export async function fetchSceneBytesWithRetry(
    url: string,
    config: Partial<GuardConfig> = {},
    deps: FetchDeps = {}
): Promise<{ bytes: Uint8Array; contentType: string; stats: GuardRunStats }> {
    const cfg = { ...DEFAULT_CONFIG, ...config };
    const fetchImpl = deps.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
    if (!fetchImpl) throw new Error('chunkFetchGuard: no fetch implementation available');
    const now = deps.now ?? (() => Date.now());
    const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

    const stats: GuardRunStats = { attempts: 0, retries: 0, stalls: 0, resumes: 0, bytesFinal: 0, bytesWasted: 0, msTotal: now() };
    let received: Uint8Array[] = [];
    let receivedBytes = 0;
    let contentType = 'application/octet-stream';

    for (let attempt = 0; attempt < cfg.maxAttempts; attempt++) {
        stats.attempts = attempt + 1;
        if (attempt > 0) stats.retries++;
        const resuming = attempt > 0 && receivedBytes > 0;
        if (resuming) stats.resumes++;

        const controller = new AbortController();
        const headers: Record<string, string> = {};
        if (resuming) headers['Range'] = `bytes=${receivedBytes}-`;

        let response: Response;
        try {
            // 响应头阶段看门狗：fetch() 本身无超时，用 headerMs 兜底
            response = await new Promise<Response>((resolve, reject) => {
                const t = setTimeout(() => {
                    controller.abort();
                    reject(new Error(`header stall >${cfg.headerMs}ms`));
                }, cfg.headerMs);
                fetchImpl(url, { headers, signal: controller.signal, cache: 'default', credentials: 'same-origin' }).then(
                    (r) => { clearTimeout(t); resolve(r); },
                    (e) => { clearTimeout(t); reject(e); }
                );
            });
        } catch (err) {
            const reason = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
            stats.bytesWasted += receivedBytes;
            if (attempt + 1 < cfg.maxAttempts) {
                await backoffSleep(attempt, cfg, sleep);
                continue;
            }
            throw new Error(`chunk-fetch give-up (fetch) ${url}: ${reason}`);
        }

        if (response.status >= 400) {
            // 4xx 永久失败立即交还；5xx 可重试
            response.body?.cancel().catch(() => {});
            stats.bytesWasted += receivedBytes;
            if (response.status < 500 || attempt + 1 >= cfg.maxAttempts) {
                const err = new Error(`chunk-fetch HTTP ${response.status} ${url}`);
                (err as Error & { status?: number }).status = response.status;
                throw err;
            }
            await backoffSleep(attempt, cfg, sleep);
            continue;
        }

        // 200 意味着 Range 被忽略（或不曾续传）——推倒已收字节重来
        if (response.status === 200 || !resuming) {
            if (resuming) stats.bytesWasted += receivedBytes;
            received = [];
            receivedBytes = 0;
        }
        const contentTypeHeader = response.headers.get('content-type');
        if (contentTypeHeader) contentType = contentTypeHeader;

        const declaredTotal = parseDeclaredTotal(response);
        if (!response.body) {
            // 无流（极老环境）：一次性取，无看门狗
            const buf = new Uint8Array(await response.arrayBuffer());
            if (declaredTotal !== null && buf.byteLength !== declaredTotal && attempt + 1 < cfg.maxAttempts) {
                received = [buf]; receivedBytes = buf.byteLength;
                await backoffSleep(attempt, cfg, sleep);
                continue;
            }
            stats.bytesFinal = buf.byteLength;
            stats.msTotal = now() - stats.msTotal;
            return { bytes: buf, contentType, stats };
        }

        // —— 流式读取 + 停滞看门狗 ——
        const reader = response.body.getReader();
        let stallAborts = false;
        let lastByteAt = now();
        let truncated = false;
        try {
            for (;;) {
                let stallClear: (() => void) | undefined;
                const stallTimer = new Promise<never>((_, reject) => {
                    const ms = Math.max(1000, cfg.stallMs - (now() - lastByteAt));
                    const t = setTimeout(() => {
                        stallAborts = true;
                        controller.abort();
                        reject(new Error(`stall >${cfg.stallMs}ms`));
                    }, ms);
                    stallClear = () => clearTimeout(t);
                });
                let readResult: ReadableStreamReadResult<Uint8Array>;
                try {
                    readResult = await Promise.race([reader.read(), stallTimer]);
                } finally {
                    stallClear?.();
                }
                if (readResult.done) break;
                if (readResult.value?.byteLength) {
                    received.push(readResult.value);
                    receivedBytes += readResult.value.byteLength;
                    lastByteAt = now();
                }
            }
        } catch (err) {
            const reason = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
            if (stallAborts) stats.stalls++;
            // 已收字节保留给续传，不计浪费
            if (attempt + 1 < cfg.maxAttempts) {
                await backoffSleep(attempt, cfg, sleep);
                continue;
            }
            throw new Error(`chunk-fetch give-up (stream) ${url}: ${reason}`);
        }

        // 完整性校验：长度对不上 = 截断，续传重试
        if (declaredTotal !== null && receivedBytes < declaredTotal) {
            truncated = true;
        } else if (declaredTotal !== null && receivedBytes > declaredTotal) {
            // 服务器数据异常，整体重来
            stats.bytesWasted += receivedBytes;
            received = []; receivedBytes = 0;
            truncated = true;
        }
        if (truncated) {
            if (attempt + 1 < cfg.maxAttempts) {
                await backoffSleep(attempt, cfg, sleep);
                continue;
            }
            throw new Error(`chunk-fetch truncated ${url}: ${receivedBytes}/${declaredTotal ?? '?'}`);
        }

        stats.bytesFinal = receivedBytes;
        stats.msTotal = now() - stats.msTotal;
        return { bytes: concatChunks(received, receivedBytes), contentType, stats };
    }
    throw new Error(`chunk-fetch exhausted attempts ${url}`);
}

function backoffSleep(attempt: number, cfg: GuardConfig, sleep: (ms: number) => Promise<void>): Promise<void> {
    const exp = cfg.backoffBaseMs * Math.pow(2, attempt);
    const capped = Math.min(cfg.backoffCapMs, exp);
    const jitter = capped * cfg.backoffJitter * Math.random();
    return sleep(capped + jitter);
}

function parseDeclaredTotal(response: Response): number | null {
    const contentRange = response.headers.get('content-range');
    if (contentRange) {
        const m = /\/(\d+|\*)\s*$/.exec(contentRange);
        if (m && m[1] !== '*') return parseInt(m[1], 10);
        return null;
    }
    const len = response.headers.get('content-length');
    if (len) return parseInt(len, 10);
    return null;
}

function concatChunks(chunks: Uint8Array[], total: number): Uint8Array {
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { out.set(c, off); off += c.byteLength; }
    return out;
}

/** 转成引擎 http.get 回调期望的响应形态（对齐引擎 _onSuccess 语义）。 */
export function toEngineResponse(bytes: Uint8Array, contentType: string, responseType: unknown, urlPath: string): unknown {
    const rt = typeof responseType === 'string' ? responseType.toLowerCase() : '';
    const looksJson = rt === 'json' || contentType.split(';')[0].trim() === 'application/json' || urlPath.split('?')[0].endsWith('.json');
    // TS5.9 的 Uint8Array 泛型 buffer 可能是 SharedArrayBuffer，BlobPart 只收
    // ArrayBuffer——slice 出独立 ArrayBuffer 同时天然做一次拷贝隔离
    const copy = (): ArrayBuffer => bytes.slice().buffer as ArrayBuffer;
    if (rt === 'blob') return new Blob([copy()], { type: contentType });
    if (rt === 'arraybuffer') return copy();
    if (looksJson) return JSON.parse(new TextDecoder().decode(bytes));
    return new TextDecoder().decode(bytes);
}

// —— 并发信号量（仅本层接管的请求）——
let activeGuarded = 0;
const semaphoreQueue: (() => void)[] = [];
function acquireSlot(max: number): Promise<() => void> {
    if (activeGuarded < max) {
        activeGuarded++;
        return Promise.resolve(() => releaseSlot());
    }
    return new Promise((resolve) => {
        semaphoreQueue.push(() => {
            activeGuarded++;
            resolve(() => releaseSlot());
        });
    });
}
function releaseSlot(): void {
    activeGuarded--;
    const next = semaphoreQueue.shift();
    if (next) next();
}

/** e2e/线上观测探针（headless 配方：页内断言优先于 CDP console）。 */
export interface ChunkFetchStats {
    requests: number;
    retries: number;
    stalls: number;
    resumes: number;
    fiveHxx: number;
    failures: number;
    ok: number;
    passthrough: number;
}
function statsObject(): ChunkFetchStats {
    const w = typeof window !== 'undefined' ? (window as unknown as { __chunkFetchStats?: ChunkFetchStats }) : undefined;
    if (w && !w.__chunkFetchStats) {
        w.__chunkFetchStats = { requests: 0, retries: 0, stalls: 0, resumes: 0, fiveHxx: 0, failures: 0, ok: 0, passthrough: 0 };
    }
    return w?.__chunkFetchStats ?? { requests: 0, retries: 0, stalls: 0, resumes: 0, fiveHxx: 0, failures: 0, ok: 0, passthrough: 0 };
}

let installed = false;
/**
 * 安装到引擎 http 单例：场景分块 GET 走硬化层，其余原样直通。
 * main.ts 最早处调用一次（先于任何资产加载）。
 */
export function installSceneChunkFetchGuard(httpLike: HttpLike, config: Partial<GuardConfig> = {}): void {
    if (installed) return;
    installed = true;
    const cfg = { ...DEFAULT_CONFIG, ...config };
    const originalGet = httpLike.get.bind(httpLike);
    const guarded: typeof httpLike.get = (url, options, callback) => {
        const cb = typeof callback === 'function' ? callback : (options as { callback?: (e: unknown, r: unknown) => void } | undefined)?.callback;
        const method = typeof options === 'object' && options !== null ? ((options as Record<string, unknown>).method as string | undefined) : undefined;
        const normalizedUrl = typeof url === 'string' ? url : ((url as { load?: string } | null)?.load ?? '');
        if (!cb || !shouldGuardSceneUrl(normalizedUrl, method ?? 'GET')) {
            statsObject().passthrough++;
            return originalGet(url, options, callback);
        }
        const s = statsObject();
        s.requests++;
        const opts = (typeof options === 'object' && options !== null ? options : {}) as Record<string, unknown>;
        let requestUrl = normalizedUrl;
        // 引擎语义：cache===false 追加 ts= 破缓存（场景 json 保持 no-cache 即时性）
        if (opts.cache === false) {
            const sep = requestUrl.includes('?') ? '&' : '?';
            requestUrl = `${requestUrl}${sep}ts=${Date.now()}`;
        }
        const responseType = opts.responseType;
        const progress = opts.progress as { fire?: (name: string, received: number, total: number) => void } | undefined;
        let urlPath = requestUrl;
        try { urlPath = new URL(requestUrl, (typeof location !== 'undefined' && location.href) || 'http://local/').pathname; } catch { /* keep raw */ }

        void (async () => {
            try {
                const release = await acquireSlot(cfg.maxConcurrent);
                try {
                    const { bytes, contentType, stats } = await fetchSceneBytesWithRetry(requestUrl, cfg);
                    s.retries += stats.retries; s.stalls += stats.stalls; s.resumes += stats.resumes;
                    s.ok++;
                    try { progress?.fire?.('progress', bytes.byteLength, bytes.byteLength); } catch { /* 引擎侧异常不影响交付 */ }
                    cb(null, toEngineResponse(bytes, contentType, responseType, urlPath));
                } finally {
                    release();
                }
            } catch (err) {
                s.failures++;
                const status = (err as { status?: number }).status;
                if (typeof status === 'number' && status >= 500) s.fiveHxx++;
                console.warn(`[chunk-fetch] ${err instanceof Error ? err.message : String(err)}`);
                cb(err, null);
            }
        })();
        // 引擎调用方（ImgParser/JsonHandler）忽略返回值；无 XHR 句柄可给
        return undefined;
    };
    httpLike.get = guarded;
    console.info(`[chunk-fetch] guard installed (stall=${cfg.stallMs}ms attempts=${cfg.maxAttempts} concurrent=${cfg.maxConcurrent})`);
}
